import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, Loader2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your payment...');

  useEffect(() => {
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');

    if (paymentId && payerId) {
      executePayment(paymentId, payerId);
    } else {
      setStatus('error');
      setMessage('Invalid payment parameters. Please try again.');
    }
  }, [searchParams]);

  const executePayment = async (paymentId, payerId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/paypal/execute-payment?payment_id=${paymentId}&payer_id=${payerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatus('success');
      setMessage(response.data.message || 'Payment successful! Your subscription is now active.');
      toast.success('Payment completed successfully!');

      // Clear the cart
      try {
        await axios.delete(`${API_URL}/api/organizer/cart/clear`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        // Cart might already be cleared
      }
    } catch (error) {
      console.error('Payment execution error:', error);
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Payment failed. Please contact support.');
      toast.error('Payment failed');
    }
  };

  return (
    <OrganizerLayout>
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="flex flex-col items-center gap-4">
              {status === 'processing' && (
                <>
                  <Loader2 className="h-16 w-16 text-blue-500 animate-spin" />
                  <span className="text-xl">Processing Payment</span>
                </>
              )}
              {status === 'success' && (
                <>
                  <CheckCircle2 className="h-16 w-16 text-green-500" />
                  <span className="text-xl text-green-700">Payment Successful!</span>
                </>
              )}
              {status === 'error' && (
                <>
                  <XCircle className="h-16 w-16 text-red-500" />
                  <span className="text-xl text-red-700">Payment Failed</span>
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            <p className="text-gray-600">{message}</p>
            
            {status === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700 text-sm">
                  Your subscription has been activated. You now have access to all premium features.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">
                  If you were charged, please contact support with your PayPal transaction ID.
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              {status !== 'processing' && (
                <Button onClick={() => navigate('/organizer')} className="bg-maroon hover:bg-maroon-hover">
                  <Home className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </Button>
              )}
              {status === 'error' && (
                <Button variant="outline" onClick={() => navigate('/organizer/cart')}>
                  Try Again
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </OrganizerLayout>
  );
};

export default PaymentSuccess;
